'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface NavItem {
  labelKey: string;
  href: string;
  icon: string;
}

interface NavGroup {
  titleKey: string;
  items: NavItem[];
}

const customerNav: NavGroup[] = [
  {
    titleKey: 'main',
    items: [
      { labelKey: 'dashboard', href: '/sender-home', icon: 'HomeIcon' },
      { labelKey: 'createOrder', href: '/create-order', icon: 'PlusCircleIcon' },
      { labelKey: 'marketplace', href: '/marketplace', icon: 'ShoppingBagIcon' },
      { labelKey: 'browseTrips', href: '/browse-trips', icon: 'MapIcon' },
      { labelKey: 'myOrders', href: '/my-orders', icon: 'ClipboardDocumentListIcon' },
    ],
  },
  {
    titleKey: 'deliveries',
    items: [
      { labelKey: 'activeDeliveries', href: '/active-deliveries', icon: 'TruckIcon' },
      { labelKey: 'bidDetails', href: '/accepted-bid-details', icon: 'ClipboardDocumentCheckIcon' },
      { labelKey: 'liveTracking', href: '/live-tracking', icon: 'SignalIcon' },
      { labelKey: 'storage', href: '/storage-listings', icon: 'BuildingStorefrontIcon' },
      { labelKey: 'travelChat', href: '/delivery-messaging', icon: 'ChatBubbleLeftRightIcon' },
    ],
  },
  {
    titleKey: 'finance',
    items: [
      { labelKey: 'myFinances', href: '/user-dashboard', icon: 'WalletIcon' },
      { labelKey: 'payments', href: '/payments-escrow', icon: 'BanknotesIcon' },
      { labelKey: 'notifications', href: '/notifications', icon: 'BellIcon' },
    ],
  },
  {
    titleKey: 'account',
    items: [
      { labelKey: 'disputes', href: '/disputes', icon: 'ShieldExclamationIcon' },
      { labelKey: 'reviews', href: '/reviews', icon: 'StarIcon' },
      { labelKey: 'helpCenter', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
      { labelKey: 'profile', href: '/profile', icon: 'UserCircleIcon' },
    ],
  },
];

const travelerNav: NavGroup[] = [
  {
    titleKey: 'main',
    items: [
      { labelKey: 'dashboard', href: '/traveler-home', icon: 'HomeIcon' },
      { labelKey: 'publishTrip', href: '/create-trip', icon: 'PlusCircleIcon' },
      { labelKey: 'myTrips', href: '/my-trips', icon: 'MapIcon' },
      { labelKey: 'createOrder', href: '/create-order', icon: 'ArchiveBoxIcon' },
      { labelKey: 'marketplace', href: '/marketplace', icon: 'ClipboardDocumentListIcon' },
      { labelKey: 'myBids', href: '/my-bids', icon: 'TagIcon' },
    ],
  },
  {
    titleKey: 'deliveries',
    items: [
      { labelKey: 'activeDeliveries', href: '/active-deliveries', icon: 'TruckIcon' },
      { labelKey: 'deliveryDetails', href: '/accepted-bid-details', icon: 'ClipboardDocumentCheckIcon' },
      { labelKey: 'liveTracking', href: '/live-tracking', icon: 'SignalIcon' },
      { labelKey: 'storage', href: '/storage-listings', icon: 'BuildingStorefrontIcon' },
      { labelKey: 'travelChat', href: '/delivery-messaging', icon: 'ChatBubbleLeftRightIcon' },
    ],
  },
  {
    titleKey: 'finance',
    items: [
      { labelKey: 'myFinances', href: '/user-dashboard', icon: 'WalletIcon' },
      { labelKey: 'earnings', href: '/traveler-earnings', icon: 'BanknotesIcon' },
      { labelKey: 'wallet', href: '/payments-escrow', icon: 'CreditCardIcon' },
      { labelKey: 'notifications', href: '/notifications', icon: 'BellIcon' },
    ],
  },
  {
    titleKey: 'account',
    items: [
      { labelKey: 'disputes', href: '/disputes', icon: 'ShieldExclamationIcon' },
      { labelKey: 'reviews', href: '/reviews', icon: 'StarIcon' },
      { labelKey: 'getVerified', href: '/traveler-onboarding', icon: 'ShieldCheckIcon' },
      { labelKey: 'helpCenter', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
      { labelKey: 'profile', href: '/profile', icon: 'UserCircleIcon' },
    ],
  },
];

const studentNav: NavGroup[] = [
  {
    titleKey: 'main',
    items: [
      { labelKey: 'dashboard', href: '/traveler-home', icon: 'HomeIcon' },
      { labelKey: 'publishTrip', href: '/create-trip', icon: 'PlusCircleIcon' },
      { labelKey: 'myTrips', href: '/my-trips', icon: 'MapIcon' },
      { labelKey: 'createOrder', href: '/create-order', icon: 'ArchiveBoxIcon' },
      { labelKey: 'marketplace', href: '/marketplace', icon: 'ClipboardDocumentListIcon' },
      { labelKey: 'myBids', href: '/my-bids', icon: 'TagIcon' },
    ],
  },
  {
    titleKey: 'deliveries',
    items: [
      { labelKey: 'activeDeliveries', href: '/active-deliveries', icon: 'TruckIcon' },
      { labelKey: 'liveTracking', href: '/live-tracking', icon: 'SignalIcon' },
      { labelKey: 'travelChat', href: '/delivery-messaging', icon: 'ChatBubbleLeftRightIcon' },
    ],
  },
  {
    titleKey: 'finance',
    items: [
      { labelKey: 'myFinances', href: '/user-dashboard', icon: 'WalletIcon' },
      { labelKey: 'earnings', href: '/traveler-earnings', icon: 'BanknotesIcon' },
      { labelKey: 'wallet', href: '/payments-escrow', icon: 'CreditCardIcon' },
      { labelKey: 'notifications', href: '/notifications', icon: 'BellIcon' },
    ],
  },
  {
    titleKey: 'account',
    items: [
      { labelKey: 'disputes', href: '/disputes', icon: 'ShieldExclamationIcon' },
      { labelKey: 'reviews', href: '/reviews', icon: 'StarIcon' },
      { labelKey: 'studentKyc', href: '/student-kyc', icon: 'IdentificationIcon' },
      { labelKey: 'getVerified', href: '/traveler-onboarding', icon: 'AcademicCapIcon' },
      { labelKey: 'helpCenter', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
      { labelKey: 'profile', href: '/profile', icon: 'UserCircleIcon' },
    ],
  },
];

// Admin and host navs use static labels (not translated for admin-only views)
const adminNavStatic = [
  { title: 'Overview', items: [{ label: 'Dashboard', href: '/admin-dashboard', icon: 'HomeIcon' }] },
  { title: 'Operations', items: [
    { label: 'User Management', href: '/admin-dashboard?tab=users', icon: 'UsersIcon' },
    { label: 'User Profiles', href: '/admin-dashboard?tab=user-profiles', icon: 'UserCircleIcon' },
    { label: 'Trip Review', href: '/admin-dashboard?tab=trip-review', icon: 'MapIcon' },
    { label: 'ID Verification', href: '/admin-dashboard?tab=id-verification', icon: 'IdentificationIcon' },
    { label: 'Student KYC', href: '/admin-dashboard?tab=student-kyc', icon: 'AcademicCapIcon' },
    { label: 'Courier Approval', href: '/admin-dashboard?tab=courier-approval', icon: 'TruckIcon' },
    { label: 'Disputes', href: '/admin-dashboard?tab=disputes', icon: 'ExclamationTriangleIcon' },
    { label: 'Station Moderators', href: '/station-moderator', icon: 'BuildingOffice2Icon' },
    { label: 'Orders', href: '/marketplace', icon: 'ClipboardDocumentListIcon' },
    { label: 'Storage', href: '/storage-listings', icon: 'BuildingStorefrontIcon' },
  ]},
  { title: 'Finance', items: [{ label: 'Payments & Escrow', href: '/payments-escrow', icon: 'BanknotesIcon' }] },
  { title: 'Intelligence', items: [
    { label: 'Analytics', href: '/admin-dashboard?tab=analytics', icon: 'PresentationChartLineIcon' },
    { label: 'Reviews', href: '/reviews', icon: 'StarIcon' },
  ]},
  { title: 'System', items: [
    { label: 'Notifications', href: '/notifications', icon: 'BellIcon' },
    { label: 'Help Center', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
    { label: 'Settings', href: '/profile', icon: 'Cog6ToothIcon' },
  ]},
];

const hostNavStatic = [
  { title: 'Main', items: [
    { label: 'Dashboard', href: '/host-home', icon: 'HomeIcon' },
    { label: 'Provider Dashboard', href: '/storage-provider', icon: 'ChartBarIcon' },
    { label: 'Storage Units', href: '/storage-listings', icon: 'BuildingStorefrontIcon' },
  ]},
  { title: 'Storage Orders', items: [
    { label: 'Create Order', href: '/create-order', icon: 'PlusCircleIcon' },
    { label: 'My Orders', href: '/my-orders', icon: 'ClipboardDocumentListIcon' },
    { label: 'Active Deliveries', href: '/active-deliveries', icon: 'TruckIcon' },
    { label: 'Live Tracking', href: '/live-tracking', icon: 'SignalIcon' },
  ]},
  { title: 'Communication', items: [
    { label: 'Messages', href: '/delivery-messaging', icon: 'ChatBubbleLeftRightIcon' },
    { label: 'Notifications', href: '/notifications', icon: 'BellIcon' },
  ]},
  { title: 'Finance', items: [{ label: 'Earnings', href: '/storage-provider', icon: 'BanknotesIcon' }] },
  { title: 'Account', items: [
    { label: 'Reviews', href: '/reviews', icon: 'StarIcon' },
    { label: 'Help Center', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
    { label: 'Profile', href: '/profile', icon: 'UserCircleIcon' },
  ]},
];

const moderatorNavStatic = [
  { title: 'Main', items: [{ label: 'Station Dashboard', href: '/station-moderator', icon: 'HomeIcon' }] },
  { title: 'Operations', items: [
    { label: 'Active Deliveries', href: '/active-deliveries', icon: 'TruckIcon' },
    { label: 'Live Tracking', href: '/live-tracking', icon: 'SignalIcon' },
    { label: 'Disputes', href: '/disputes', icon: 'ShieldExclamationIcon' },
    { label: 'Notifications', href: '/notifications', icon: 'BellIcon' },
  ]},
  { title: 'Account', items: [
    { label: 'Help Center', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
    { label: 'Profile', href: '/profile', icon: 'UserCircleIcon' },
  ]},
];

const courierNavStatic = [
  { title: 'Main', items: [
    { label: 'Dashboard', href: '/courier-dashboard', icon: 'HomeIcon' },
    { label: 'Browse Orders', href: '/marketplace', icon: 'ClipboardDocumentListIcon' },
    { label: 'Post a Trip', href: '/create-trip', icon: 'PlusCircleIcon' },
    { label: 'My Trips', href: '/my-trips', icon: 'MapIcon' },
    { label: 'My Bids', href: '/my-bids', icon: 'TagIcon' },
  ]},
  { title: 'Deliveries', items: [
    { label: 'Active Deliveries', href: '/active-deliveries', icon: 'TruckIcon' },
    { label: 'Delivery Details', href: '/accepted-bid-details', icon: 'ClipboardDocumentCheckIcon' },
    { label: 'Live Tracking', href: '/live-tracking', icon: 'SignalIcon' },
    { label: 'Messages', href: '/delivery-messaging', icon: 'ChatBubbleLeftRightIcon' },
  ]},
  { title: 'Finance', items: [
    { label: 'Earnings', href: '/traveler-earnings', icon: 'BanknotesIcon' },
    { label: 'Wallet', href: '/payments-escrow', icon: 'CreditCardIcon' },
    { label: 'Notifications', href: '/notifications', icon: 'BellIcon' },
  ]},
  { title: 'Account', items: [
    { label: 'Disputes', href: '/disputes', icon: 'ShieldExclamationIcon' },
    { label: 'Reviews', href: '/reviews', icon: 'StarIcon' },
    { label: 'Courier Registration', href: '/courier-onboarding', icon: 'IdentificationIcon' },
    { label: 'Help Center', href: '/help-center', icon: 'QuestionMarkCircleIcon' },
    { label: 'Profile', href: '/profile', icon: 'UserCircleIcon' },
  ]},
];

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose: () => void;
  role: 'sender' | 'customer' | 'traveler' | 'host' | 'admin' | 'station_moderator' | 'student' | 'courier';
  displayName?: string;
  avatarUrl?: string;
}

export default function Sidebar({ collapsed, onToggleCollapse, onMobileClose, role, displayName, avatarUrl }: SidebarProps) {
  const pathname = usePathname();
  const { t } = useLanguage();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    const path = href.split('?')[0];
    return pathname.startsWith(path);
  };

  const roleLabel: Record<string, string> = {
    sender: 'Customer',
    customer: 'Customer',
    traveler: 'Traveler',
    student: 'Student Traveler',
    host: 'Storage Host',
    admin: 'Administrator',
    station_moderator: 'Station Moderator',
    courier: 'Courier',
  };

  const initials = displayName
    ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : role[0].toUpperCase();

  // For translated navs, resolve label from translation key
  const getLabel = (labelKey: string): string => {
    return (t as any)[labelKey] || labelKey;
  };

  const getGroupTitle = (titleKey: string): string => {
    return (t as any)[titleKey] || titleKey;
  };

  // Determine which nav to use
  const useTranslatedNav = ['sender', 'customer', 'traveler', 'student'].includes(role);
  const translatedNavGroups = role === 'traveler' ? travelerNav : role === 'student' ? studentNav : customerNav;
  const staticNavGroups = role === 'admin' ? adminNavStatic : role === 'host' ? hostNavStatic : role === 'courier' ? courierNavStatic : moderatorNavStatic;

  return (
    <div
      className={`
        flex flex-col h-full bg-card border-r border-border
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-72 lg:w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'justify-center px-2' : ''}`}>
        <div className="flex-shrink-0">
          {!collapsed && <AppLogo size={32} />}
          {collapsed && (
            <span className="font-heading font-extrabold text-primary text-sm tracking-tight">M</span>
          )}
        </div>
        {/* Close button: always visible on mobile, hidden on desktop */}
        <button
          onClick={onMobileClose}
          className="ml-auto lg:hidden p-2.5 hover:bg-muted rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Close sidebar"
        >
          <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
        </button>
      </div>

      {/* User profile + collapse */}
      <div className="border-b border-border p-2 space-y-1">
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-700 flex-shrink-0 overflow-hidden ring-2 ring-primary/20">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName || role} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 truncate text-foreground">{displayName || roleLabel[role] || role}</p>
              <p className="text-[11px] text-muted-foreground truncate capitalize">{roleLabel[role] || role}</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center py-1">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-700 flex-shrink-0 overflow-hidden ring-2 ring-primary/20">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName || role} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex w-full items-center justify-center gap-2 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={16} />
          {!collapsed && <span className="text-xs font-500">Collapse</span>}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {useTranslatedNav ? (
          translatedNavGroups.map((group) => (
            <div key={`group-${group.titleKey}`}>
              {!collapsed && (
                <p className="text-[11px] font-600 tracking-widest uppercase text-muted-foreground px-2 mb-1">
                  {getGroupTitle(group.titleKey)}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <Link
                    key={`nav-${item.href}-${item.labelKey}`}
                    href={item.href}
                    onClick={onMobileClose}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      sidebar-item-hover relative group min-h-[44px]
                      ${isActive(item.href)
                        ? 'sidebar-item-active' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                      }
                      ${collapsed ? 'justify-center px-2' : ''}
                    `}
                    title={collapsed ? getLabel(item.labelKey) : undefined}
                  >
                    <Icon
                      name={item.icon as Parameters<typeof Icon>[0]['name']}
                      size={20}
                      className={isActive(item.href) ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                    />
                    {!collapsed && (
                      <span className="flex-1 truncate">{getLabel(item.labelKey)}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))
        ) : (
          staticNavGroups.map((group) => (
            <div key={`group-${group.title}`}>
              {!collapsed && (
                <p className="text-[11px] font-600 tracking-widest uppercase text-muted-foreground px-2 mb-1">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <Link
                    key={`nav-${item.href}-${item.label}`}
                    href={item.href}
                    onClick={onMobileClose}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      sidebar-item-hover relative group min-h-[44px]
                      ${isActive(item.href)
                        ? 'sidebar-item-active' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                      }
                      ${collapsed ? 'justify-center px-2' : ''}
                    `}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon
                      name={item.icon as Parameters<typeof Icon>[0]['name']}
                      size={20}
                      className={isActive(item.href) ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                    />
                    {!collapsed && (
                      <span className="flex-1 truncate">{item.label}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))
        )}
      </nav>
    </div>
  );
}